import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import CategoryIcon from "@/components/CategoryIcon";
import { Badges } from "@/components/Badges";
import { useApp } from "@/context/AppContext";
import API from "@/lib/api";
import MapView from "@/components/MapView";
import { slugify } from "@/lib/slug";
import { distanceLabel } from "@/lib/format";
import { Search, Star, MapPin, Phone, Globe, Navigation, MessageSquare, FileText, BadgeCheck, Plus, Users, X, List, Map as MapIcon, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { SectionSuggestions } from "@/components/SectionSuggestions";

export default function Directory() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { categories, catName, getCategory } = useApp();
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [verified, setVerified] = useState(false);
  const [minRating, setMinRating] = useState(0);
  const [items, setItems] = useState(null);
  const [zone, setZone] = useState(null);
  const [view, setView] = useState("list");
  const [sponsored, setSponsored] = useState([]);

  useEffect(() => { API.get("/me/full").then((r) => setZone(r.data.zone)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    const sp = zone?.start_point;
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (category) qs.set("category", category);
    if (verified) qs.set("verified", "true");
    if (minRating) qs.set("min_rating", String(minRating));
    if (sp?.lat) { qs.set("lat", sp.lat); qs.set("lng", sp.lng); }
    const { data } = await API.get(`/businesses?${qs.toString()}`);
    setItems(data);
    const spq = new URLSearchParams();
    if (category) spq.set("category", category);
    try {
      const s = await API.get(`/sponsored?${spq.toString()}`);
      const seen = new Set();
      setSponsored(s.data.filter((b) => (seen.has(b.id) ? false : seen.add(b.id))));
    } catch { setSponsored([]); }
  }, [q, category, verified, minRating, zone]);

  useEffect(() => { load(); }, [load]);

  const cat = getCategory(category);

  return (
    <Layout>
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-serif text-2xl font-bold text-[#0F1230]">{"Cerca un'attività"}</h1>
        <button data-testid="suggest-business-button" onClick={() => navigate("/suggest-business")} className="inline-flex items-center gap-1 text-sm text-[#141A3A] font-medium"><Plus size={15} /> Aggiungi</button>
      </div>

      <div className="relative mb-3">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4E68]" />
        <Input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="es. gommista, ripetizioni inglese" className="pl-10 bg-white h-12 rounded-full" />
      </div>

      {q.trim().length >= 2 && <SectionSuggestions query={q} title="Forse cerchi questa sezione" />}

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-3">
        <button data-testid="dir-cat-all" onClick={() => setCategory("")} className={`shrink-0 px-3 h-9 rounded-full text-sm border ${!category ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA] bg-white"}`}>Tutte</button>
        {categories.filter((c) => c.slug !== "varie").map((c) => (
          <button key={c.slug} data-testid={`dir-cat-${c.slug}`} onClick={() => setCategory(c.slug === category ? "" : c.slug)} className={`shrink-0 px-3 h-9 rounded-full text-sm border ${category === c.slug ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA] bg-white"}`}>{catName(c)}</button>
        ))}
      </div>

      <div className="flex items-center gap-3 mb-4 text-sm">
        <label className="inline-flex items-center gap-1.5"><Switch data-testid="filter-verified" checked={verified} onCheckedChange={setVerified} /> Verificate</label>
        <label className="inline-flex items-center gap-1.5"><Switch data-testid="filter-rating" checked={minRating >= 4} onCheckedChange={(v) => setMinRating(v ? 4 : 0)} /> 4★ e più</label>
        <div className="ml-auto flex bg-[#EDEDF4] rounded-full p-1">
          <button data-testid="view-list" onClick={() => setView("list")} className={`inline-flex items-center gap-1 px-3 h-8 rounded-full text-sm ${view === "list" ? "bg-white text-[#141A3A] font-medium" : "text-[#4A4E68]"}`}><List size={15} /> Elenco</button>
          <button data-testid="view-map" onClick={() => setView("map")} className={`inline-flex items-center gap-1 px-3 h-8 rounded-full text-sm ${view === "map" ? "bg-white text-[#141A3A] font-medium" : "text-[#4A4E68]"}`}><MapIcon size={15} /> Mappa</button>
        </div>
      </div>

      <button data-testid="promote-cta" onClick={() => navigate("/promuovi")} className="w-full mb-4 rounded-[18px] border border-dashed border-[#E8590C]/60 bg-[#FFF1E6] text-[#B8420A] text-sm font-medium h-11 inline-flex items-center justify-center gap-1.5 hover:bg-[#FFE3CF] transition-colors">
        <Sparkles size={15} /> Hai un'attività? Mettila in evidenza
      </button>

      {sponsored.length > 0 && view === "list" && (
        <div data-testid="sponsored-section" className="mb-4">
          <p className="text-xs uppercase tracking-wide text-[#4A4E68] mb-2 inline-flex items-center gap-1"><Sparkles size={13} /> Sponsorizzato</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sponsored.map((b) => (
              <div key={`sp-${b.id}`} className="relative">
                <span className="absolute z-[1] top-2 right-2 text-[10px] bg-[#E8590C] text-white px-2 py-0.5 rounded-full">Sponsorizzato</span>
                <BusinessCard b={b} onOpen={() => navigate(`/business/${b.id}`)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {items === null ? <p className="text-[#4A4E68]">Caricamento...</p> : (
        <>
          {view === "map" ? (
            <MapView items={items} onSelect={(b) => navigate(`/business/${b.id}`)} />
          ) : items.length === 0 ? (
            <p className="text-[#4A4E68]">Nessuna attività trovata.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {items.map((b) => <BusinessCard key={b.id} b={b} onOpen={() => navigate(`/business/${b.id}`)} />)}
            </div>
          )}
          {category && zone?.start_point?.city && (
            <button data-testid="seo-page-link" onClick={() => navigate(`/attivita/${category}/${slugify(zone.start_point.city)}`)} className="mt-3 text-sm text-[#141A3A] underline">
              Vedi la pagina pubblica: {cat ? catName(cat) : category} a {zone.start_point.city}
            </button>
          )}
          {category && (
            <button data-testid="ask-all-zone" onClick={() => navigate(`/publish/${category}`)} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#E8590C] text-white rounded-full h-12 font-medium">
              <Users size={18} /> Chiedi a tutti quelli della zona
            </button>
          )}
        </>
      )}
    </Layout>
  );
}

function BusinessCard({ b, onOpen }) {
  const navigate = useNavigate();
  const { getCategory, catName } = useApp();
  const cat = getCategory(b.category);
  const contacts = b.public_contacts || {};
  const askQuote = (e) => { e.stopPropagation(); navigate(`/publish/${b.category}?business=${b.id}&businessName=${encodeURIComponent(b.trade_name)}`); };
  const stop = (e) => e.stopPropagation();
  return (
    <div data-testid={`business-card-${b.id}`} onClick={onOpen} className="cursor-pointer bg-white rounded-[18px] border border-[#DADFEA] p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-[#0F1230] leading-tight">{b.trade_name}</h3>
          <p className="text-sm text-[#4A4E68]">{cat ? catName(cat) : b.category}{b.city ? ` · ${b.city}` : ""}{b.distance_km != null ? ` · ${distanceLabel(b.distance_km)}` : ""}</p>
        </div>
        {b.avg_rating != null && <span className="text-sm text-[#4A4E68] inline-flex items-center gap-0.5 shrink-0"><Star size={14} className="fill-[#E8590C] text-[#E8590C]" /> {b.avg_rating} ({b.review_count})</span>}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5 items-center">
        {b.admin_verified && <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] text-[#2E7D32] px-2 py-0.5 text-xs font-medium"><BadgeCheck size={13} /> Verificata</span>}
        {!b.claimed && <span className="inline-flex items-center gap-1 rounded-full bg-[#EDEDF4] text-[#4A4E68] px-2 py-0.5 text-xs">Scheda non rivendicata</span>}
      </div>
      {b.hours && <p className="text-xs text-[#4A4E68] mt-2">{b.hours}</p>}
      <div className="flex flex-wrap gap-2 mt-3">
        {contacts.phone && <a onClick={stop} href={`tel:${contacts.phone}`} className="inline-flex items-center gap-1 text-sm border border-[#DADFEA] rounded-full px-3 h-9"><Phone size={14} /> Chiama</a>}
        <a onClick={stop} href={`https://www.openstreetmap.org/search?query=${encodeURIComponent((b.trade_name || "") + " " + (b.city || ""))}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm border border-[#DADFEA] rounded-full px-3 h-9"><Navigation size={14} /> Indicazioni</a>
        {contacts.website && <a onClick={stop} href={contacts.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm border border-[#DADFEA] rounded-full px-3 h-9"><Globe size={14} /> Sito</a>}
        <button data-testid={`ask-quote-${b.id}`} onClick={askQuote} className="inline-flex items-center gap-1 text-sm bg-[#1F4FE0] text-white rounded-full px-3 h-9"><FileText size={14} /> Chiedi un preventivo</button>
      </div>
    </div>
  );
}
